import React, { forwardRef, useId, useRef, useEffect } from 'react';
import { cn } from '../theme';

// ============================================================================
// TEXTAREA
// ============================================================================

export interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  /**
   * Label displayed above the textarea.
   */
  label?: string;
  /**
   * Helper text displayed below the textarea.
   */
  helperText?: string;
  /**
   * Error message.
   */
  error?: string;
  /**
   * If true, textarea will auto-resize to fit content.
   */
  autoResize?: boolean;
  /**
   * Maximum height when auto-resizing (in pixels).
   */
  maxHeight?: number;
}

/**
 * Textarea component with label and error support.
 * 
 * @example
 * // Basic textarea
 * <Textarea placeholder="Enter your message..." />
 * 
 * // With label
 * <Textarea label="Description" rows={4} />
 * 
 * // Auto-resize
 * <Textarea autoResize label="Notes" />
 */
const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      className,
      label,
      helperText,
      error,
      autoResize = false,
      maxHeight = 300,
      id: providedId,
      disabled,
      onChange,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const id = providedId ?? generatedId;
    const hasError = !!error;
    const internalRef = useRef<HTMLTextAreaElement>(null);

    // Handle auto-resize
    const handleResize = () => {
      const textarea = internalRef.current;
      if (textarea && autoResize) {
        textarea.style.height = 'auto';
        const newHeight = Math.min(textarea.scrollHeight, maxHeight);
        textarea.style.height = `${newHeight}px`;
      }
    };

    useEffect(() => {
      handleResize();
    }, [props.value, autoResize]);

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (autoResize) {
        handleResize();
      }
      onChange?.(e);
    };

    // Merge refs
    const setRefs = (element: HTMLTextAreaElement | null) => {
      internalRef.current = element;
      if (typeof ref === 'function') {
        ref(element);
      } else if (ref) {
        ref.current = element;
      }
    };

    return (
      <div className="flex flex-col gap-1.5 w-full">
        {label && (
          <label
            htmlFor={id}
            className={cn(
              'font-medium text-sm text-navy',
              disabled && 'opacity-60'
            )}
          >
            {label}
          </label>
        )}

        <textarea
          ref={setRefs}
          id={id}
          disabled={disabled}
          onChange={handleChange}
          className={cn(
            'w-full min-h-[100px] bg-cream border-2 border-transparent rounded-md',
            'px-4 py-3 font-body text-base text-navy placeholder:text-warm-gray',
            'transition-all duration-200 resize-y',
            'focus:outline-none focus:border-sage focus:ring-4 focus:ring-sage/15',
            'hover:border-sage-light',
            'disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:border-transparent',
            hasError && 'border-red-400 focus:border-red-500 focus:ring-red-500/15 hover:border-red-400',
            autoResize && 'resize-none overflow-hidden',
            className
          )}
          aria-invalid={hasError}
          aria-describedby={
            error ? `${id}-error` : helperText ? `${id}-helper` : undefined
          }
          {...props}
        />

        {error && (
          <p
            id={`${id}-error`}
            className="text-sm text-red-500"
            role="alert"
          >
            {error}
          </p>
        )}

        {helperText && !error && (
          <p id={`${id}-helper`} className="text-sm text-warm-gray">
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';

// ============================================================================
// EXPORTS
// ============================================================================

export { Textarea };
